import {
  cloudfaasServiceUrl,
  defaultLensAlias,
  defaultPillarId,
} from '../../../../common/utils/constants'
import { API } from '../../../../common/api'
import {
  CreateWAFRAssessmentResponse,
  CreateWAFRAssessmentRequest,
  WAFRAssessmentFormDetails,
} from '../../../modules/wafrAssessment/reducer'
import { Answer } from '../../../modules/wafrQuestionAnswerDetails/reducer'
import { AxiosResponse } from 'axios'
import { PillarWorkloadDetails } from '../../../modules/wafrPillars/reducer'
import { AwsAccountIDs } from '../../../modules/wafrCreateAssessment/reducer'
import { ShareWorkloadPayload } from '../../../modules/wafrShareWorkload/reducer'

export const createWAFRAssessment = (
  partnerId: string,
  assessmentData: CreateWAFRAssessmentRequest
): Promise<AxiosResponse<CreateWAFRAssessmentResponse>> => {
  return API.post(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload`,
    assessmentData
  )
}

export const fetchWAFRQueAnswer = (
  partnerId: string,
  workloadId: string,
  wafrQuestionId: string,
  lensAlias = defaultLensAlias
) => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/question/${wafrQuestionId}/${lensAlias}`
  )
}

export const updateWAFRAnswer = (
  partnerId: string,
  workloadId: string,
  wafrQuestionId: string,
  updateAnswerPayload: Answer,
  lensAlias = defaultLensAlias
) => {
  return API.patch(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/answers/${wafrQuestionId}/${lensAlias}`,
    updateAnswerPayload
  )
}

export const updateWAFRAssessment = (
  partnerId: string,
  workloadId: string,
  assessmentData: CreateWAFRAssessmentRequest
): Promise<AxiosResponse<CreateWAFRAssessmentResponse>> => {
  return API.patch(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}`,
    assessmentData
  )
}

export const fetchWAFRAWSAccounts = (
  partnerId: string
): Promise<AxiosResponse<AwsAccountIDs[]>> => {
  return API.get(`${cloudfaasServiceUrl}/partners/${partnerId}/aws_accounts`)
}

export const fetchWAFRWorkloadById = (
  partnerId: string,
  workloadId: string
): Promise<AxiosResponse<WAFRAssessmentFormDetails>> => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}`
  )
}

export const fetchLensReview = (
  partnerId: string,
  workloadId: string,
  lensAlias = defaultLensAlias
): Promise<AxiosResponse<PillarWorkloadDetails>> => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/review/${lensAlias}`
  )
}

export const downloadLensReport = (
  partnerId: string,
  workloadId: string,
  lensAlias = defaultLensAlias
) => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/report/${lensAlias}`
  )
}
export const getWAFRAssessmentQuestions = (
  partnerId: string,
  workloadId: string,
  lensAlias = defaultLensAlias,
  pillarId = defaultPillarId,
  rowsPerSize = 15
) => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/${lensAlias}/answers?rows_per_size=${rowsPerSize}&pillar_id=${pillarId}`
  )
}

export const deleteWorkload = (partnerId: string, workloadId: string) => {
  return API.delete(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}`
  )
}
export const getFindingsSummary = (
  partnerId: string,
  assessmentId: string,
  filter?: string,
  filterId?: string
) => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/assessments/${assessmentId}/checks/summary`,
    {
      params: {
        ...(filter && { filter_level: filter }),
        ...(filterId && { filter_id: filterId }),
      },
    }
  )
}

export const refreshFindings = (partnerId: string, assessmentId: string) => {
  return API.post(
    `${cloudfaasServiceUrl}/partners/${partnerId}/assessments/${assessmentId}/checks/refresh`
  )
}

export const getFindingsList = (
  partnerId: string,
  assessmentId: string,
  limit = 10,
  pillar?: string[],
  status?: string[],
  question?: string[],
  bestPracticeId?: string[],
  region?: string[],
  awsAccountId?: string[],
  paginationToken?: string,
  downloadReport?: boolean
) => {
  const params: Record<string, any> = {
    limit: limit,
    ...(paginationToken && { pagination_token: paginationToken }),
    ...(downloadReport && { download_report: downloadReport }),
  }

  // Handle arrays for multiple values
  if (pillar?.length) {
    params[`pillars`] = pillar
  }
  if (status?.length) {
    params[`status`] = status
  }
  if (region?.length) {
    params[`regions`] = region
  }
  if (awsAccountId?.length) {
    params[`aws_accounts`] = awsAccountId
  }
  if (question?.length) {
    params[`questions`] = question
  }
  if (bestPracticeId?.length) {
    params[`best_practices`] = bestPracticeId
  }

  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/assessments/${assessmentId}/checks/findings`,
    {
      params,
      paramsSerializer: params => {
        return Object.entries(params)
          .reduce((acc, [key, value]) => {
            if (Array.isArray(value)) {
              value.forEach(v => acc.push(`${key}=${v}`))
            } else {
              acc.push(`${key}=${value}`)
            }
            return acc
          }, [] as string[])
          .join('&')
      },
    }
  )
}

export const suppressFinding = (
  partnerId: string,
  assessmentId: string,
  findings?: string[],
  action?: string
) => {
  return API.post(
    `${cloudfaasServiceUrl}/partners/${partnerId}/assessments/${assessmentId}/checks/findings/suppress`,
    {
      finding_ids: findings,
      action: action,
    }
  )
}

export const getStatus = (partnerId: string, workloadId: string) => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/status`
  )
}

export const updateFlag = (
  partnerId: string,
  workloadId: string,
  wafrQuestionId: string,
  isFlagged: boolean
) => {
  return API.patch(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/question/${wafrQuestionId}`,
    {
      is_flagged: isFlagged,
    }
  )
}

export const shareWorkload = (
  partnerId: string,
  workloadId: string,
  shareWorkloadPayload: ShareWorkloadPayload
) => {
  return API.post(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/share`,
    shareWorkloadPayload
  )
}

export const fetchSharedWorkloadList = (
  partnerId: string,
  workloadId: string,
  rowsPerSize = 30
) => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/share?rows_per_size=${rowsPerSize}`
  )
}

export const deleteSharedWorkload = (
  partnerId: string,
  workloadId: string,
  shareId: string
) => {
  return API.delete(
    `${cloudfaasServiceUrl}/partners/${partnerId}/workload/${workloadId}/share/${shareId}`
  )
}

export const getComments = (
  partnerId: string,
  workloadId: string,
  questionId: string,
  limit = 5,
  nextToken?: string
) => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/assessments/${workloadId}/comments`,
    {
      params: {
        ...(questionId && { question_id: questionId }),
        ...(limit && { limit: limit }),
        ...(nextToken && { next_token: nextToken }),
      },
    }
  )
}

export const sendComment = (
  partnerId: string,
  workloadId: string,
  questionId: string,
  firstName: string,
  lastName: string,
  userId: string,
  comment: string
) => {
  return API.post(
    `${cloudfaasServiceUrl}/partners/${partnerId}/assessments/${workloadId}/comments`,
    {
      firstname: firstName,
      lastname: lastName,
      user_id: userId,
      message: comment,
      question_id: questionId,
    }
  )
}

export const downloadCISReport = (partnerId: string, workloadId: string) => {
  return API.get(
    `${cloudfaasServiceUrl}/partners/${partnerId}/assessments/${workloadId}/cis-checks/report`
  )
}
